import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    loadJobSectorDetails,
    loadPoolAcceptedRejectionReasonsDetails,
    loadPoolRequestedRejectionReasonsDetails,
    loadPoolStatusDetails
} from '../../../../store/directusService';
import {
    clearDocumentDetailsSearch,
    searchDocumentDetails
} from '../../../../store/documentService';
import { clearParticipantsSearch, searchParticipants } from '../../../../store/participantService';
import {
    loadPoolApplicationsByPoolIds,
    loadPoolsByUser,
    updatePoolApplicationsAccepted
} from '../../../../store/recruitmentService';
import { searchUsersByLoggedInUserServiceIds } from '../../../../store/userService';
import { getNameFromId, getObjFromId } from '../../../../utils/directusFunctions';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../../utils/formValidation/commonConstants';
import { NO_UPDATE_ERROR } from '../../../../utils/formValidation/commonErrorConstants';
import {
    JOB_SECTOR_DETAILS_ERROR,
    POOL_ACCEPTED_REJECTION_REASONS_DETAILS_ERROR,
    POOL_REQUESTED_REJECTION_REASONS_DETAILS_ERROR,
    POOL_STATUS_DETAILS_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { clearKeys } from '../../../../utils/objectUtils';
import { stableSort } from '../../../../utils/sortFunctions';
import { uuid } from '../../../../utils/stringUtils';
import { addEmailAddressAsNameToArray } from '../../../../utils/userArrayUtils';
import {
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../utils/userRoles';
import Button from '../../../formElements/Button';
import MultiSelect from '../../../formElements/MultiSelect';
import SingleSelect from '../../../formElements/SingleSelect';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import PoolApplicationRejectEditor from './PoolApplicationRejectEditor';
import PoolEditor from './PoolEditor';
import PoolTableRow from './PoolTableRow';

import app from '../../../../app.module.css';
import classes from '../../recruitmentStyles/pool.module.css';

const headCells = [
    { id: 'ptCode', numeric: false, label: 'ID' },
    { id: 'ptName', numeric: false, label: 'Participant Name' },
    { id: 'filename', numeric: false, label: 'CV' },
    { id: 'downloadCV', numeric: false, label: 'Download CV' },
    { id: 'startDate', numeric: false, label: 'Pool Start Date' },
    { id: 'jobSectorName', numeric: false, label: 'Job Sector' },
    { id: 'statusName', numeric: false, label: 'Status' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'startDate',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const acceptedRoles = [MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const poolOwnerRoles = [MANAGER, RECRUITMENT_MANAGER];

const PoolTableManagement = () => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const [poolRows, setPoolRows] = useState([]);
    const [rows, setRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [rejectedRow, setRejectedRow] = useState({});
    const [rowCancelled, setRowCancelled] = useState(false);
    const [poolApplicationsLoaded, setPoolApplicationsLoaded] = useState(false);
    const [poolEditor, setPoolEditor] = useState(false);
    const [poolOwners, setPoolOwners] = useState([]);
    const [selectedPoolOwner, setSelectedPoolOwner] = useState('');
    const [updatedPoolApplications, setUpdatedPoolApplications] = useState([]);
    const [poolApplicationRejectEditor, setPoolApplicationRejectEditor] = useState(false);
    const [teamDetails, setTeamDetails] = useState([]);
    const [isDisabled, setIsDisabled] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [creatingPool, setCreatingPool] = useState(false);
    const [searchParticipantIds, setSearchParticipantIds] = useState([]);

    const [arrayJobSectors, setArrayJobSectors] = useState([]);
    const [jobSectorPools, setJobSectorPools] = useState([]);
    const [preSelectedJobSectorIds, setPreSelectedJobSectorIds] = useState([]);
    const [selectedJobSectorIds, setSelectedJobSectorIds] = useState([]);
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [openId, setOpenId] = useState('');

    const [keys, setKeys] = useState({ sectorIds: '0', ownerId: '1' });

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const jobSectorDetails = useSelector(
        (state) => state.entities.directusService.jobSectorDetails
    );
    const poolAcceptedRejectionReasonsDetails = useSelector(
        (state) => state.entities.directusService.poolAcceptedRejectionReasonsDetails
    );
    const poolRequestedRejectionReasonsDetails = useSelector(
        (state) => state.entities.directusService.poolRequestedRejectionReasonsDetails
    );
    const poolStatusDetails = useSelector(
        (state) => state.entities.directusService.poolStatusDetails
    );
    const participantDocuments = useSelector(
        (state) => state.entities.documentService.documentDetailsSearch
    );
    const loadingParticipantDocuments = useSelector(
        (state) => state.entities.documentService.loadingDocumentDetailsSearch
    );
    const participantDocumentsMetaData = useSelector(
        (state) => state.entities.documentService.documentDetailsSearchMetaData
    );
    const participantsSearch = useSelector(
        (state) => state.entities.participantService.participantsSearch
    );
    const loadingParticipantsSearch = useSelector(
        (state) => state.entities.participantService.loadingParticipantsSearch
    );
    const participantsSearchMetaData = useSelector(
        (state) => state.entities.participantService.participantsSearchMetaData
    );
    const poolApplications = useSelector(
        (state) => state.entities.recruitmentService.poolApplications
    );
    const pools = useSelector((state) => state.entities.recruitmentService.pools);
    const userMetaData = useSelector(
        (state) => state.entities.userService.usersByLoggedInUserServiceIdsMetaData
    );
    const users = useSelector((state) => state.entities.userService.usersByLoggedInUserServiceIds);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        jobSectorDetails?.length < 1 && dispatch(loadJobSectorDetails());
        poolAcceptedRejectionReasonsDetails?.length < 1 &&
            dispatch(loadPoolAcceptedRejectionReasonsDetails());
        poolRequestedRejectionReasonsDetails?.length < 1 &&
            dispatch(loadPoolRequestedRejectionReasonsDetails());
        poolStatusDetails?.length < 1 && dispatch(loadPoolStatusDetails());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (!loggedInUser || Object.keys(loggedInUser).length < 1) return;

        if (loggedInUser.roles.some((el) => poolOwnerRoles.includes(el))) {
            setSelectedPoolOwner(loggedInUser.id);
            configPoolOwnersArray([loggedInUser]);
        }
        setIsActive(hasRole(acceptedRoles, loggedInUser.roles));
    }, [loggedInUser]);

    useEffect(() => {
        if (!users.length) return;

        let updatedPoolOwners = users.filter((el) =>
            el.userTypes?.find((entry) => poolOwnerRoles.includes(entry.role))
        );
        if (!poolOwners.length && users.length < userMetaData.totalElements) {
            onLoadMorePoolOwners();
            return;
        }
        if (selectedPoolOwner && !updatedPoolOwners.some((el) => el.id === selectedPoolOwner)) {
            // add selected pool owner to top of DDL
            const poolOwner = poolOwners.find((el) => el.id === selectedPoolOwner);
            if (poolOwner) updatedPoolOwners = [poolOwner, ...updatedPoolOwners];
        }
        configPoolOwnersArray(updatedPoolOwners);
    }, [users]);

    useEffect(() => {
        if (!selectedPoolOwner) return;
        dispatch(loadPoolsByUser(selectedPoolOwner));
        setJobSectorPools([]);
        getTeams(selectedPoolOwner);
    }, [selectedPoolOwner]);

    useEffect(() => {
        if (pools?.length < 1) {
            setPoolRows([]);
            setRows([]);
            setFilteredRows([]);
            setUpdatedPoolApplications([]);
            setPoolApplicationsLoaded(true);
            return;
        }

        setPoolApplicationsLoaded(false);
        const filteredPools = pools.filter((el) => el.ownerId === selectedPoolOwner);

        if (!filteredPools) {
            setPoolRows([]);
            setRows([]);
            setPoolApplicationsLoaded(true);
            setJobSectorPools([]);
            return;
        }

        const jobSectors = jobSectorDetails.filter((el) =>
            filteredPools.find((entry) => el.id === entry.jobSectorId)
        );

        const newFilteredRows = filteredPools.map((el) => ({
            ...el,
            id: uuid(),
            poolId: el.id,
            jobSectorName: getNameFromId(jobSectors, el.jobSectorId)
        }));

        setJobSectorPools(jobSectors);

        creatingPool
            ? setCreatingPool(false)
            : dispatch(loadPoolApplicationsByPoolIds(filteredPools.map((el) => el.id)));

        setPoolRows(newFilteredRows);
    }, [pools]);

    useEffect(() => {
        if (jobSectorDetails.length < 1 || poolStatusDetails.length < 1)
            return;
        if (poolApplications.length < 1) {
            setRows(poolRows);
            setFilteredRows(poolRows);
            setUpdatedPoolApplications(poolRows);
        } else {
            let updatedPoolsWithApplications = [...poolRows];
            !poolApplicationsLoaded && setPoolApplicationsLoaded(true);
            poolApplications.forEach((el) => {
                let newPoolEntryArray = [];
                const status = getObjFromId(poolStatusDetails, el.statusId) || {};
                updatedPoolsWithApplications = updatedPoolsWithApplications.map((entry) => {
                    if (entry.poolId === el.participantPoolId) {
                        if (entry.participantId) {
                            const newPoolEntry = {
                                id: uuid(),
                                ownerId: entry.ownerId,
                                jobSectorId: entry.jobSectorId,
                                jobSectorName: entry.jobSectorName,
                                teamIds: entry.teamIds,
                                poolId: entry.poolId,
                                requirements: entry.requirements,
                                ptCode: '',
                                ptName: '',
                                filename: '',
                                participantPoolApplicationId: el.id,
                                participantId: el.participantId,
                                goldStandardCVId: el.goldStandardCVId,
                                startDate: el.startDate || '',
                                statusId: el.statusId,
                                statusName: status?.name || '',
                                status,
                                reasons: el.rejection?.rejectionIds.map((el) => el) || '',
                                notes: el.rejection?.notes || ''
                            };
                            // Check for duplicate
                            if (newPoolEntryArray.length > 0) {
                                if (
                                    newPoolEntryArray.some(
                                        (npe) =>
                                            npe.jobSectorId === newPoolEntry.jobSectorId &&
                                            npe.participantPoolApplicationId ===
                                                newPoolEntry.participantPoolApplicationId
                                    )
                                ) {
                                    return entry;
                                }
                            }
                            newPoolEntryArray = [...newPoolEntryArray, newPoolEntry];
                            return entry;
                        } else {
                            return {
                                ...entry,
                                ptCode: '',
                                ptName: '',
                                filename: '',
                                participantPoolApplicationId: el.id,
                                participantId: el.participantId,
                                goldStandardCVId: el.goldStandardCVId,
                                startDate: el.startDate || '',
                                statusId: el.statusId,
                                statusName: status?.name || '',
                                status,
                                reasons: el.rejection?.rejectionIds?.map((el) => el) || '',
                                notes: el.rejection?.notes || ''
                            };
                        }
                    } else {
                        if (entry.participantId) {
                            return entry;
                        } else {
                            return {
                                ...entry,
                                ptCode: '',
                                ptName: '',
                                filename: '',
                                participantPoolApplicationId: null,
                                participantId: null,
                                goldStandardCVId: null,
                                startDate: '',
                                statusId: '',
                                statusName: '',
                                status: {},
                                reasons: [],
                                notes: ''
                            };
                        }
                    }
                });
                if (newPoolEntryArray?.length > 0) {
                    updatedPoolsWithApplications = [
                        ...updatedPoolsWithApplications,
                        ...newPoolEntryArray
                    ];
                }
            });
            setUpdatedPoolApplications(updatedPoolsWithApplications);

            const participantIds = [
                ...new Set(updatedPoolsWithApplications.map((el) => el.participantId))
            ].filter((el) => el);
            if (participantIds.length) {
                setSearchParticipantIds(participantIds);
                dispatch(searchParticipants({ ids: participantIds }, 0));
                dispatch(
                    searchDocumentDetails(
                        {
                            participantIds,
                            types: ['gold standard cv']
                        },
                        0
                    )
                );
            } else {
                setRows(updatedPoolsWithApplications);
                setFilteredRows(updatedPoolsWithApplications);
            }
        }
    }, [poolApplications, jobSectorDetails, poolStatusDetails, poolRows]);

    useEffect(() => {
        if (
            searchParticipantIds.length &&
            participantsSearch?.length > 0 &&
            !loadingParticipantsSearch &&
            'number' in participantsSearchMetaData &&
            !participantsSearchMetaData.last
        ) {
            dispatch(
                searchParticipants(
                    { ids: searchParticipantIds },
                    participantsSearchMetaData.number + 1
                )
            );
        }

        if (
            participantDocuments?.length > 0 &&
            !loadingParticipantDocuments &&
            'number' in participantDocumentsMetaData &&
            !participantDocumentsMetaData.last
        ) {
            dispatch(
                searchDocumentDetails(
                    {
                        participantIds: searchParticipantIds,
                        types: ['gold standard cv']
                    },
                    participantDocumentsMetaData.number + 1
                )
            );
        }

        if (participantsSearchMetaData.last && participantDocumentsMetaData.last) {
            const updatedRows = updatedPoolApplications.map((el) => {
                const participant = participantsSearch.find(
                    (entry) => entry.id === el.participantId
                );

                const document = participantDocuments.find(
                    (entry) =>
                        entry.participantId === participant?.id && el.goldStandardCVId === entry.id
                );

                return {
                    ...el,
                    ptCode: participant?.ptCode || '',
                    ptName: `${participant?.firstName || ''} ${participant?.lastName || ''}`,
                    filename: document?.filename || ''
                };
            });
            setUpdatedPoolApplications(updatedRows);
            setRows(updatedRows);
            setFilteredRows(updatedRows);
        }
    }, [
        participantsSearch,
        participantsSearchMetaData,
        loadingParticipantsSearch,
        participantDocuments,
        participantDocumentsMetaData,
        loadingParticipantDocuments
    ]);

    useEffect(() => {
        if (jobSectorDetails?.length && arrayJobSectors?.length === 0) {
            setPreSelectedJobSectorIds(jobSectorDetails.map((el) => el.id));
            setArrayJobSectors(jobSectorDetails);
        }
    }, [jobSectorDetails]);

    useEffect(() => {
        if (rows.length < 1 || selectedJobSectorIds.length < 1) return;

        const filteredRows = rows.filter((el) =>
            selectedJobSectorIds.some((entry) => entry === el.jobSectorId)
        );
        setFilteredRows(filteredRows);
        setUpdatedPoolApplications(filteredRows);
    }, [selectedJobSectorIds]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === `Pool applications accepted have been updated`) setIsDisabled(false);
        else if (successMessage === `Pool applications have been loaded`)
            setPoolApplicationsLoaded(true);
        setIsButtonDisabled(false);
    }, [successMessage]);

    // HELPER FNS
    const getTeams = (id) => {
        if (!id) return;

        const teamDisplay = users.find((entry) => entry.id === id);
        if (teamDisplay) {
            const { primaryTeam, otherTeams } = teamDisplay;
            if (primaryTeam) {
                setTeamDetails([primaryTeam, ...otherTeams]);
            } else {
                setTeamDetails([]);
            }
        }
    };

    const loadingError = () => {
        if (jobSectorDetails?.length < 1) return JOB_SECTOR_DETAILS_ERROR;
        if (poolAcceptedRejectionReasonsDetails?.length < 1)
            return POOL_ACCEPTED_REJECTION_REASONS_DETAILS_ERROR;
        if (poolRequestedRejectionReasonsDetails?.length < 1)
            return POOL_REQUESTED_REJECTION_REASONS_DETAILS_ERROR;
        if (poolStatusDetails?.length < 1) return POOL_STATUS_DETAILS_ERROR;
    };

    const configPoolOwnersArray = (poolOwners) => {
        const updatedPoolOwners = addEmailAddressAsNameToArray(poolOwners);
        setPoolOwners(updatedPoolOwners);
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    const cleanUp = () => {
        dispatch(clearDocumentDetailsSearch());
        dispatch(clearParticipantsSearch());
        setKeys(clearKeys(keys));
    };

    const createRows = () =>
        stableSort(filteredRows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <PoolTableRow
                key={el.id}
                row={el}
                isActive={isActive}
                toggleDropdown={setOpenId}
                openId={openId}
                onUpdateParticipantStatus={onUpdateParticipantStatus}
                onPoolApplicationRejectEditor={onPoolApplicationRejectEditor}
                onPoolRejectedRow={onPoolRejectedRow}
                rowCancelled={rowCancelled}
            />
        ));

    // EVENT HANDLERS
    const onPoolOwnerChange = (chosenId) => {
        if (!chosenId || Object.keys(chosenId).length === 0) {
            chosenId = '';
            setRows([]);
            setFilteredRows([]);
            setUpdatedPoolApplications([]);
            setJobSectorPools([]);
            setIsDisabled(true);
        } else setIsDisabled(false);
        setSelectedPoolOwner(chosenId);
    };

    const onJobSectorChange = (chosenIds) => {
        if (!chosenIds) chosenIds = [];
        setSelectedJobSectorIds(chosenIds);
    };

    const onLoadMorePoolOwners = () => {
        if ((!users.length || users.length < userMetaData.totalElements) && !userMetaData.last) {
            dispatch(
                searchUsersByLoggedInUserServiceIds(
                    loggedInUser.serviceIds,
                    !users.length ? 0 : userMetaData.number + 1
                )
            );
        }
    };

    // EVENT HANDLER CALLBACKS
    const onPoolEditor = (setOpen) => setPoolEditor(setOpen);

    const onUpdateParticipantStatus = (statusId, participantId, participantPoolApplicationId) => {
        const modified = updatedPoolApplications.map((el) =>
            el.participantId === participantId &&
            el.participantPoolApplicationId === participantPoolApplicationId
                ? { ...el, statusId, status: getObjFromId(poolStatusDetails, statusId) }
                : el
        );
        setUpdatedPoolApplications(modified);
    };

    const onPoolApplicationRejectEditor = (setOpen) => {
        setPoolApplicationRejectEditor(setOpen);
    };

    const onPoolApplicationRejectCancelled = (cancelled, rejectedRow) => {
        if (cancelled) {
            const resetRows = rows.filter((el) => el.id !== rejectedRow.id);
            setRows([...resetRows, rejectedRow]);
            setFilteredRows([...resetRows, rejectedRow]);
            setUpdatedPoolApplications([...resetRows, rejectedRow]);
            setRowCancelled(true);
        } else {
            setRowCancelled(false);
        }
    };

    const onPoolRejectedRow = (row) => {
        setRejectedRow(row);
        setRowCancelled(false);
    };

    const onPoolRejectedUpdate = (update) => {
        setPoolApplicationsLoaded(update);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const poolStatusId = poolStatusDetails.find(
            (el) => el.name.toLowerCase() === 'accepted'
        )?.id;

        const acceptedPoolApplicationIds = updatedPoolApplications
            .filter((el) => el.statusId === poolStatusId && !el.startDate)
            .map((el) => el.participantPoolApplicationId);

        if (acceptedPoolApplicationIds && acceptedPoolApplicationIds.length > 0) {
            setPoolApplicationsLoaded(false);
            setRows([]);
            setFilteredRows([]);
            setIsDisabled(true);
            dispatch(updatePoolApplicationsAccepted({ ids: acceptedPoolApplicationIds }));
        } else {
            setErrors({ button: { error: true, message: NO_UPDATE_ERROR } });
        }
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} time={3} />;

    // RENDER
    return (
        <div>
            <form onSubmit={onSubmit} data-testid="form_start">
                <div className={classes.poolToolBar}>
                    <SingleSelect
                        id={'poolOwnerId'}
                        key={keys.ownerId}
                        label={'Pool Owner'}
                        placeholder="Select Pool Owner..."
                        disabled={!hasRole(acceptedRoles, loggedInUser.roles)}
                        customClass="widthControl"
                        capitalizeList={false}
                        menuItems={poolOwners}
                        selectedId={selectedPoolOwner}
                        selected={poolOwners?.find((el) => el.id === selectedPoolOwner) || {}}
                        onLoadMoreItems={onLoadMorePoolOwners}
                        moreItemsToLoad={
                            !userMetaData.totalElements || users.length < userMetaData.totalElements
                        }
                        onChange={(chosenId) => onPoolOwnerChange(chosenId)}
                    />
                    <MultiSelect
                        id="jobSectors"
                        key={keys.sectorIds}
                        label="Job Sector"
                        placeholder="Job Sector"
                        disabled={!hasRole(acceptedRoles, loggedInUser.roles)}
                        menuItems={jobSectorDetails || []}
                        preSelectedIds={preSelectedJobSectorIds}
                        preSelects={jobSectorDetails}
                        onChange={(chosenIds) => onJobSectorChange(chosenIds)}
                    />
                    <Button
                        id="CreatePoolButton"
                        type="button"
                        customClass="toolBarButton"
                        disabled={!hasRole(acceptedRoles, loggedInUser.roles) || !selectedPoolOwner}
                        content="Create Pool"
                        icon={true}
                        onClick={() => onPoolEditor(true)}
                    />
                </div>
                {poolEditor && (
                    <PoolEditor
                        isOpen={poolEditor}
                        onPoolEditorClose={onPoolEditor}
                        jobSectorDetails={
                            jobSectorPools.length > 0 ? jobSectorPools : jobSectorDetails
                        }
                        selectedPoolOwner={selectedPoolOwner}
                        poolOwners={poolOwners}
                        teamDetails={teamDetails}
                        onCreatePool={() => setCreatingPool(true)}
                    />
                )}

                <PoolApplicationRejectEditor
                    open={poolApplicationRejectEditor}
                    onPoolApplicationRejectEditor={onPoolApplicationRejectEditor}
                    onPoolApplicationRejectCancelled={onPoolApplicationRejectCancelled}
                    updatedPoolApplications={updatedPoolApplications}
                    rejectedRow={rejectedRow}
                    onPoolRejectedUpdate={onPoolRejectedUpdate}
                />

                {filteredRows.length < 1 ? (
                    <LoadingSpinner content="No pools found" />
                ) : (
                    <div className={app.fadein}>
                        <ResultsTable
                            defaultOrderBy={rowMetaData.orderBy}
                            sortOrder={rowMetaData.order}
                            defaultRowsPerPage={rowMetaData.rowsPerPage}
                            headCells={headCells}
                            passRowMetaDataUp={setRowMetaData}
                            tableRows={createRows()}
                            totalResults={filteredRows.length}
                        />
                    </div>
                )}

                <Button
                    id="submitButton"
                    disabled={
                        !hasRole(acceptedRoles, loggedInUser.roles) ||
                        isDisabled ||
                        isButtonDisabled
                    }
                    content="Update"
                    error={errors?.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

export default PoolTableManagement;
